/* The Italian Chocolate 1971 · tic-site.js (home sections). No dependencies.
   1. Scroll reveals: one IntersectionObserver for [data-tic-reveal] inside .tic sections; elements entering together
      are staggered (data-tic-stagger on an ancestor, default 40ms, clamped to 30-80ms), once.
   2. Seasonal blocks: [data-tic-from] / [data-tic-until] (YYYYMMDD) re-checked in the browser.
   3. Quick add: form[data-tic-quick-add] -> POST /cart/add.js over fetch, then the theme's
      <cart-drawer> renders and opens. Without the drawer or fetch the form posts normally
      (native POST /cart/add, the shop goes to /cart). */
(() => {
  if (window.TIC) return;
  // TIC.ready turns true only once init() has run: the inline fallback in tic-site-head checks it,
  // so if this file throws half-way the reveals are disarmed and nothing stays hidden.
  window.TIC = { v: 2, ready: false };
  const doc = document;
  const root = doc.documentElement;
  const design = !!(window.Shopify && window.Shopify.designMode);
  const off = root.hasAttribute('data-tic-off');
  if (!off) root.classList.add('tic-js');
  // iOS Safari applies :active (the 0.96 press scale) only when a touch listener exists.
  doc.addEventListener('touchstart', () => {}, { passive: true });

  /* 1. Reveal */
  const show = (el) => el.classList.add('is-in');
  const io = !off && !design && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .map((e) => [e.target, e.boundingClientRect])
          .sort((a, b) => Math.round(a[1].top - b[1].top) || a[1].left - b[1].left)
          .forEach(([el], i) => {
            io.unobserve(el);
            pending.delete(el);
            const host = el.parentElement && el.parentElement.closest('[data-tic-stagger]');
            const step = Math.min(80, Math.max(30, host ? Number(host.dataset.ticStagger) || 40 : 40)); // 30-80ms
            el.style.setProperty('--tic-d', Math.min(i, 8) * step + 'ms');
            show(el);
          });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0 })
    : null;
  const pending = new Set();
  const reveal = (scope) =>
    scope.querySelectorAll('.tic [data-tic-reveal]:not(.is-in)').forEach((el) => {
      if (!io) return show(el);
      pending.add(el);
      io.observe(el);
    });
  // A fast fling can skip a block between two frames (the observer never sees it on screen):
  // anything already scrolled past is shown at once, so nothing above the viewport stays hidden.
  let raf = 0;
  window.addEventListener('scroll', () => {
    if (raf || !pending.size) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      pending.forEach((el) => {
        if (el.getBoundingClientRect().bottom < 0) {
          io.unobserve(el);
          pending.delete(el);
          show(el);
        }
      });
    });
  }, { passive: true });

  /* 2. Seasonal dates (the server already decided; this covers cached pages) */
  const now = new Date();
  const today = now.getFullYear() * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
  const season = (scope) => {
    if (design) return;
    scope.querySelectorAll('.tic[data-tic-from], .tic[data-tic-until], .tic [data-tic-from], .tic [data-tic-until]').forEach((el) => {
      const from = Number(el.dataset.ticFrom) || 0;
      const until = Number(el.dataset.ticUntil) || 99991231;
      const hide = today < from || today > until;
      const target = el.hasAttribute('data-tic-section') ? el.closest('.shopify-section') || el : el;
      target.hidden = hide;
    });
  };

  /* 3. Quick add */
  const setState = (btn, state) => {
    btn.dataset.state = state;
    btn.setAttribute('aria-busy', state === 'busy' ? 'true' : 'false');
  };
  // Shopify cart errors ({status, message, description}) are written for shoppers; anything else
  // (network failure, non-JSON answer) gets the theme's generic cart error, never a technical message.
  const errorText = (err) =>
    (err && err.status && typeof err.description === 'string' && err.description) ||
    (err && err.status && typeof err.message === 'string' && err.message) ||
    (window.cartStrings && window.cartStrings.error) ||
    'This box could not be added. Please try again.';

  doc.addEventListener('submit', async (event) => {
    const form = event.target.closest && event.target.closest('form[data-tic-quick-add]');
    if (!form) return;
    const drawer = doc.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function' || !window.fetch) return; // native POST
    event.preventDefault();

    const btn = form.querySelector('[type="submit"]');
    if (!btn || btn.dataset.state === 'busy') return;
    const card = form.closest('[data-tic-card]') || form.parentElement;
    const msg = card.querySelector('[data-tic-error]');
    if (msg) {
      msg.hidden = true;
      msg.textContent = '';
    }
    setState(btn, 'busy');

    const body = new FormData(form);
    const sections = typeof drawer.getSectionsToRender === 'function'
      ? drawer.getSectionsToRender().map((s) => s.id)
      : ['cart-drawer', 'cart-icon-bubble'];
    body.append('sections', sections);
    body.append('sections_url', window.location.pathname);
    if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(btn);

    const routes = window.routes || {};
    try {
      // Cart AJAX API: /cart/add.js always answers JSON (market prefix kept, e.g. /en-de/cart/add.js).
      const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await res.json();
      if (data.status || !res.ok) throw data;

      setState(btn, 'done');
      drawer.classList.remove('is-empty');
      // Same order as the theme's product-form.js: publish BEFORE renderContents. The theme's
      // <cart-drawer-items> subscribes to cartUpdate and refills itself from the CART PAGE section
      // (?section_id=main-cart). Published after the render, that refill would land in the new
      // drawer list and swap it for the cart-page markup; published before, it only reaches the
      // list that renderContents is about to replace.
      if (typeof window.publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        window.publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tic-quick-add', productVariantId: body.get('id') });
      }
      drawer.renderContents(data);
      doc.dispatchEvent(new CustomEvent('tic:cart:added', { detail: { variantId: body.get('id'), item: data } }));
      fetch((routes.cart_url || '/cart') + '.js')
        .then((r) => r.json())
        .then((cart) => doc.dispatchEvent(new CustomEvent('cart:change', { detail: { cart } })))
        .catch(() => {});
      setTimeout(() => setState(btn, 'idle'), 2400);
    } catch (err) {
      setState(btn, 'idle');
      if (msg) {
        msg.textContent = errorText(err);
        msg.hidden = false;
      }
    }
  });

  /* 4. Card photo carousel (21 Oct 2026): [data-tic-car] > [data-tic-car-track] + prev/next + dots.
        Delegated, so cards re-rendered by other scripts keep working. */
  const carIndex = (t) => Math.round(t.scrollLeft / Math.max(1, t.clientWidth));
  const carSync = (car) => {
    const t = car.querySelector('[data-tic-car-track]');
    if (!t) return;
    const i = carIndex(t);
    const n = t.children.length;
    car.querySelectorAll('.tic-card__dot').forEach((d, k) => d.classList.toggle('is-on', k === i));
    const p = car.querySelector('[data-tic-car-prev]');
    const x = car.querySelector('[data-tic-car-next]');
    if (p) p.disabled = i <= 0;
    if (x) x.disabled = i >= n - 1;
  };
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  doc.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-tic-car-prev], [data-tic-car-next]');
    if (!btn) return;
    const car = btn.closest('[data-tic-car]');
    const t = car && car.querySelector('[data-tic-car-track]');
    if (!t) return;
    e.preventDefault();
    const dir = btn.hasAttribute('data-tic-car-next') ? 1 : -1;
    t.scrollTo({ left: (carIndex(t) + dir) * t.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
  });
  let carRaf = 0;
  doc.addEventListener('scroll', (e) => {
    const t = e.target;
    if (!t || !t.matches || !t.matches('[data-tic-car-track]')) return;
    cancelAnimationFrame(carRaf);
    carRaf = requestAnimationFrame(() => carSync(t.closest('[data-tic-car]')));
  }, true);
  const carInit = (scope) => scope.querySelectorAll('[data-tic-car]').forEach(carSync);

  /* Init + theme editor */
  const init = (scope) => {
    if (design || off) scope.querySelectorAll('.tic [data-tic-reveal]').forEach(show);
    else reveal(scope);
    season(scope);
    carInit(scope);
  };
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  init(doc);
  window.TIC.ready = true;
})();
